/**
 * FP3-069 follow-up #11 — Tiny status pill on a supervising
 * provider's caseload row showing that a student is being served
 * by one of their supervised assistants.
 *
 * Driven by `student.assistant_covered` populated by the
 * `GET /api/students` endpoint (mirror of FP3-069 follow-up #9
 * wizard-context shape). The supervising primary's row also renders
 * the existing `AssistantAssignmentChip` (picker); this pill is a
 * READ-ONLY status indicator for non-supervising viewers (admin,
 * super_admin) AND a fall-through label when the picker can't render
 * (e.g. an admin shadowing the supervisor's caseload).
 *
 * The supervising primary themselves will still see the existing
 * clickable chip — this pill only renders when the chip is not
 * already shown for the row.
 */
import { UsersThree } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";

export default function AssistantCoveredPill({ student, variant = "card" }) {
  const { user } = useAuth();
  if (!student?.assistant_covered) return null;
  // The supervising primary already sees the interactive
  // `AssistantAssignmentChip` on the same row (picker + current
  // assistant name). Suppress the static pill for them to avoid
  // a redundant "Assigned to Max · Max · SLPA" stack. Every other
  // viewer (admin, super_admin, assistant, non-supervising primary)
  // gets the pill so they can see at a glance which student is
  // assistant-served.
  const isSupervisingPrimary =
    (user?.permission_role || "").toLowerCase() === "provider"
    && (user?.provider_role_type || "").toLowerCase() === "primary";
  if (isSupervisingPrimary) return null;
  const names = student.assistant_covered_provider_names || [];
  const label = names.length ? names.join(" + ") : "Assistant";
  const title = names.length
    ? `Service delivered by ${label}`
    : "Service delivered by a supervised assistant";

  // Same palette as the Wizard Scope pill (FP3-069 follow-up #9)
  // so users see a consistent "assistant-covered" visual language
  // across Caseload + Wizard.
  const baseClasses =
    "inline-flex items-center gap-1 rounded-full font-medium border";
  const palette =
    "bg-[#EEF1F6] text-[#3F4756] border-[#3F4756]/20";

  if (variant === "row") {
    return (
      <span
        data-testid={`student-${student.id}-assistant-covered-pill`}
        title={title}
        className={`${baseClasses} ${palette} text-[10px] px-1.5 py-0.5`}
      >
        <UsersThree size={9} weight="duotone" />
        Assigned to {label}
      </span>
    );
  }
  return (
    <span
      data-testid={`student-${student.id}-assistant-covered-pill`}
      title={title}
      className={`${baseClasses} ${palette} text-[10.5px] px-2 py-0.5`}
    >
      <UsersThree size={10} weight="duotone" />
      Assigned to {label}
    </span>
  );
}
